import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LogoMark } from '../brand/Logo';
import { useSlowFlag } from '../../hooks/useSlowFlag';

const MESSAGES = {
  search: [
    'Walking through the warehouse aisles…',
    'Matching your words against 2,300 products…',
    'Looking at photos for the right look…',
    'Dusting off the shelves…',
    'Almost there, sorting the best matches first…',
  ],
  ai: [
    'Asking the AI to read your request…',
    'Turning your words into filters…',
    'Checking colours, materials and quantities…',
    'Looking at photos for the right look…',
    'Picking the best matches for you…',
  ],
  visual: [
    'Studying your photo…',
    'Comparing shapes and colours…',
    'Comparing photos of 2,300 products…',
    'Finding the closest look-alikes…',
  ],
  substitute: [
    'Looking at the requested product…',
    'Searching our stuck stock for a match…',
    'Comparing type, size, colour and price…',
    'Comparing photos side by side…',
  ],
  curate: [
    'The AI is looking at the photos…',
    'Weighing look, size, price and stock…',
    'Picking the most credible alternatives…',
    'Writing a message for your client…',
    'Polishing the wording…',
  ],
};

/**
 * Friendly overlay for slow searches. Shows only after the work has taken a
 * while (see useSlowFlag), rotates context-aware messages and can be closed:
 * the request keeps running and the results appear when ready.
 */
export default function WaitingModal({ active, mode = 'search', delay = 1500 }) {
  const slow = useSlowFlag(active, delay);
  const [dismissed, setDismissed] = useState(false);
  const [idx, setIdx] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const messages = MESSAGES[mode] || MESSAGES.search;

  useEffect(() => {
    if (!active) {
      setDismissed(false);
      setIdx(0);
      setSeconds(0);
    }
  }, [active]);
  useEffect(() => {
    if (!slow) return undefined;
    const rot = setInterval(() => setIdx((i) => (i + 1) % messages.length), 2400);
    const tick = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => {
      clearInterval(rot);
      clearInterval(tick);
    };
  }, [slow, messages.length]);

  if (!slow || dismissed) return null;
  const elapsed = seconds + Math.round(delay / 1000);
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/60 p-4 backdrop-blur-[2px] animate-in fade-in-0" role="status" aria-live="polite">
      <div className="w-full max-w-sm rounded-2xl border bg-card p-6 text-center shadow-xl">
        <div className="relative mx-auto h-20 w-20">
          <span className="toxy-glow absolute inset-0 rounded-full bg-amber-300/40 blur-xl" />
          <LogoMark className="toxy-bob relative h-20 w-20 text-foreground drop-shadow-sm" />
        </div>
        <p key={idx} className="mt-5 min-h-[2.75rem] text-[15px] font-medium leading-snug animate-in fade-in-0 slide-in-from-bottom-1 duration-500">
          {messages[idx]}
        </p>
        <div className="mt-3 flex justify-center gap-1.5" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <span key={i} className="toxy-dot h-1.5 w-1.5 rounded-full bg-amber-500" style={{ animationDelay: `${i * 0.18}s` }} />
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          {elapsed >= 8 ? `Still working on it (${elapsed}s). Thanks for your patience!` : 'Hang on, we are working on it.'}
        </p>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="mt-4 text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        >
          Keep browsing, show the results when ready
        </button>
      </div>
    </div>,
    document.body
  );
}
